import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { ReactComponent as MicrophoneIcon } from 'assets/svgs/microphone-icon.svg'
import { ReactComponent as TrashIcon } from 'assets/svgs/trash.svg'
import { getSpeechRecognition, speechRecognitionLang } from 'utils/audioUtils'
import { showErrorToast } from 'utils/toastHelpers'

const SILENCE_THRESHOLD = 15 // RMS amplitude threshold for silence
const INITIAL_SILENCE_SECONDS = 5 // auto-stop if no speech detected within 5s
const POST_SPEECH_SILENCE_SECONDS = 3 // auto-stop 3s after speech stops

// The first of these the browser can record (Safari has no webm); the browser's own default if none.
const RECORDING_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']

interface AppAudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob, transcript?: string, duration?: number) => void
  onRecordingStart?: () => void
  onRecordingStop?: () => void
  disabled?: boolean
  maxDuration?: number
  className?: string
  // BCP-47 language tag (e.g. 'en-US', 'es-ES', 'vi-VN') for Web Speech recognition.
  // Defaults to the app's current language.
  recognitionLang?: string
}

export const AppAudioRecorder: React.FC<AppAudioRecorderProps> = ({
  onRecordingComplete,
  onRecordingStart,
  onRecordingStop,
  disabled = false,
  maxDuration = 60,
  className = '',
  recognitionLang,
}) => {
  const { t, i18n } = useTranslation()
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [silenceCountdown, setSilenceCountdown] = useState(0)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const silenceAnalysisRef = useRef<number | null>(null)
  const isCancelledRef = useRef(false)
  const hasSpeechRef = useRef(false)
  const isRecordingRef = useRef(false)
  const recognitionRef = useRef<any>(null)
  const currentTranscriptRef = useRef<string>('')
  const startedAtRef = useRef(0)

  const lang = recognitionLang ?? speechRecognitionLang(i18n.language)

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (silenceTimerRef.current) clearInterval(silenceTimerRef.current)
    if (silenceAnalysisRef.current) cancelAnimationFrame(silenceAnalysisRef.current)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    analyserRef.current = null
  }, [])

  // Leaving while recording (e.g. the chat was closed) drops the recording: nothing is sent.
  useEffect(() => {
    return () => {
      isCancelledRef.current = true
      isRecordingRef.current = false
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      if (recognitionRef.current) {
        recognitionRef.current.abort?.()
        recognitionRef.current = null
      }
      cleanup()
    }
  }, [cleanup])

  // A new recognizer for each recording: a stopped one may still be finishing (starting it again
  // would throw) and must not write into the next recording's transcript.
  const startRecognition = () => {
    const SpeechRecognition = getSpeechRecognition()
    if (!SpeechRecognition) return
    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = lang

    recognition.onresult = (event: any) => {
      if (recognitionRef.current !== recognition) return
      // `results` holds every result of the recording; each pause ends a final one. Joining them
      // all keeps what was said before the last pause.
      currentTranscriptRef.current = Array.from(event.results as ArrayLike<any>)
        .map((result) => String(result[0]?.transcript ?? '').trim())
        .filter(Boolean)
        .join(' ')
    }

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
    }

    recognitionRef.current = recognition
    try {
      recognition.start()
    } catch (error) {
      console.error('Speech recognition could not start:', error)
    }
  }

  const resetSilenceCountdownTo = useCallback((seconds: number) => {
    if (silenceTimerRef.current) clearInterval(silenceTimerRef.current)
    silenceTimerRef.current = null
    setSilenceCountdown(seconds)

    let remaining = seconds
    const interval = setInterval(() => {
      remaining -= 1
      setSilenceCountdown(remaining)
      if (remaining <= 0) {
        clearInterval(interval)
        triggerStop()
      }
    }, 1000)

    silenceTimerRef.current = interval
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const triggerStop = useCallback(() => {
    if (!isRecordingRef.current) return
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
    }
    isRecordingRef.current = false
    setIsRecording(false)
    onRecordingStop?.()
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current)
      silenceTimerRef.current = null
    }
    if (silenceAnalysisRef.current) {
      cancelAnimationFrame(silenceAnalysisRef.current)
      silenceAnalysisRef.current = null
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop()
    }
    setSilenceCountdown(0)
  }, [onRecordingStop])

  const startSilenceDetection = useCallback(
    (stream: MediaStream) => {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
      if (!AudioCtx) return

      const audioContext = new AudioCtx()
      audioContextRef.current = audioContext
      const analyser = audioContext.createAnalyser()
      analyserRef.current = analyser
      analyser.fftSize = 512

      const source = audioContext.createMediaStreamSource(stream)
      source.connect(analyser)

      const bufferLength = analyser.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)

      const analyse = () => {
        if (!isRecordingRef.current) return

        analyser.getByteTimeDomainData(dataArray)

        // Compute RMS amplitude
        let sumSquares = 0
        for (let i = 0; i < bufferLength; i++) {
          const normalized = (dataArray[i] - 128) / 128
          sumSquares += normalized * normalized
        }
        const rms = Math.sqrt(sumSquares / bufferLength) * 100

        if (rms > SILENCE_THRESHOLD) {
          if (!hasSpeechRef.current) {
            // First speech detected — cancel initial silence timer and start post-speech timer
            hasSpeechRef.current = true
          }
          // Reset post-speech countdown every time we hear sound
          if (silenceTimerRef.current) {
            clearInterval(silenceTimerRef.current)
            silenceTimerRef.current = null
            setSilenceCountdown(0)
          }
          resetSilenceCountdownTo(POST_SPEECH_SILENCE_SECONDS)
        }

        silenceAnalysisRef.current = requestAnimationFrame(analyse)
      }

      silenceAnalysisRef.current = requestAnimationFrame(analyse)
    },
    [resetSilenceCountdownTo]
  )

  const startRecording = async () => {
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      })
    } catch (error) {
      console.error('Error starting recording:', error)
      showErrorToast(t('askCL.voice.micBlocked'))
      return
    }

    let mediaRecorder: MediaRecorder
    try {
      const mimeType = RECORDING_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type))
      mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    } catch (error) {
      console.error('Error starting recording:', error)
      stream.getTracks().forEach((track) => track.stop())
      showErrorToast(t('askCL.voice.micBlocked'))
      return
    }

    streamRef.current = stream
    isCancelledRef.current = false
    hasSpeechRef.current = false
    currentTranscriptRef.current = ''
    startedAtRef.current = Date.now()
    mediaRecorderRef.current = mediaRecorder
    audioChunksRef.current = []

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) audioChunksRef.current.push(event.data)
    }

    mediaRecorder.onstop = () => {
      cleanup()

      if (isCancelledRef.current) return

      const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType })
      // Measured here, not read from state: this handler belongs to the render that started it.
      const seconds = Math.round((Date.now() - startedAtRef.current) / 1000)
      const finalDuration = audioBlob.size > 0 ? Math.max(1, seconds) : 0
      const transcript = currentTranscriptRef.current || undefined

      onRecordingComplete(audioBlob, transcript, finalDuration)
    }

    mediaRecorder.start()
    isRecordingRef.current = true
    setIsRecording(true)
    setRecordingTime(0)
    onRecordingStart?.()

    // Start speech recognition for real-time transcription
    startRecognition()

    // Start recording timer
    timerRef.current = setInterval(() => {
      const seconds = Math.floor((Date.now() - startedAtRef.current) / 1000)
      setRecordingTime(seconds)
      if (seconds >= maxDuration) triggerStop()
    }, 1000)

    // Start initial silence countdown — cancelled as soon as speech is detected
    resetSilenceCountdownTo(INITIAL_SILENCE_SECONDS)

    // Start audio-level silence detection
    startSilenceDetection(stream)
  }

  const stopRecording = () => {
    triggerStop()
  }

  const cancelRecording = () => {
    isCancelledRef.current = true
    isRecordingRef.current = false
    setIsRecording(false)

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
    }

    if (recognitionRef.current) {
      recognitionRef.current.stop()
    }

    cleanup()
    setSilenceCountdown(0)
    setRecordingTime(0)
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className={`${className}`}>
      {!isRecording && (
        <AppButton
          variant="clear"
          size="sm"
          disabled={disabled}
          onClick={startRecording}
          label={t('askCL.voiceInput')}
          LeftIcon={() => <MicrophoneIcon className="w-5 h-5 text-app-grey-60" />}
        />
      )}

      {isRecording && (
        <div className="flex items-center gap-2 p-2 bg-white border border-dashed border-app-primary-40 rounded-lg min-w-[300px]">
          <button
            onClick={cancelRecording}
            className="p-1 hover:bg-app-grey-10 rounded transition-colors flex-shrink-0"
            aria-label={t('askCL.voice.cancel')}
          >
            <TrashIcon className="w-4 h-4 text-app-grey-60" />
          </button>

          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0" />

          <div className="flex items-center gap-[1px] flex-1 px-2">
            {[...Array(40)].map((_, i) => (
              <div
                key={i}
                className="w-[2px] bg-app-primary-60 rounded-full animate-pulse"
                style={{
                  height: `${Math.random() * 16 + 4}px`,
                  animationDelay: `${i * 50}ms`,
                }}
              />
            ))}
          </div>

          <div className="flex flex-col items-end flex-shrink-0 min-w-[40px]">
            <span className="text-sm text-app-grey-70 font-mono">{formatTime(recordingTime)}</span>
            {silenceCountdown > 0 && (
              <span className="text-xs text-orange-600 font-mono">{silenceCountdown}s</span>
            )}
          </div>

          <button
            onClick={stopRecording}
            className="p-1 bg-app-primary-60 hover:bg-app-primary-70 text-white rounded transition-colors flex-shrink-0"
            aria-label={t('askCL.voice.stop')}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="6" y="6" width="12" height="12" fill="currentColor" rx="2" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}
